import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ScoreRing, colorOf } from '@/components/ScoreRing';
import { Moon, Timer, Heart, Check, ChevronRight, Utensils, Snowflake, Activity, Plus, X, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useProgramLocked, LockedScreen } from '@/components/ProgramLock';
import { ProgramHeader } from '@/components/ProgramHeader';
import { RecoveryProtocol } from '@/components/RecoveryProtocol';
import { GuidedBreathingList } from '@/components/breathing/GuidedBreathingList';

export default function Recovery() {
  const locked = useProgramLocked();
  const nav = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const activitiesRef = useRef(null);
  const [rec, setRec] = useState(null);
  const [sleep, setSleep] = useState(null);
  const [fasting, setFasting] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [selfLogs, setSelfLogs] = useState([]);
  const [library, setLibrary] = useState([]);
  const [showLogger, setShowLogger] = useState(false);

  const load = () => {
    api.get('/recovery-score').then(r => setRec(r.data));
    api.get('/sleep-logs', { params: { date: today } }).then(r => setSleep(r.data)).catch(() => {});
    api.get('/fasting-logs', { params: { date: today } }).then(r => setFasting(r.data)).catch(() => {});
    api.get('/recovery/assignments').then(r => setAssignments(r.data));
    api.get('/recovery/self-logs', { params: { date: today } }).then(r => setSelfLogs(r.data)).catch(() => {});
  };
  useEffect(() => { if (!locked) { load(); api.get('/recovery/activities').then(r => setLibrary(r.data.filter(a => a.metric_type !== 'steps' && a.category !== 'Walking'))).catch(() => {}); } }, []);

  // Walking is Training — exclude step-based activities from Recovery
  const todayActs = assignments.filter(a => a.date === today && (a.activity_snapshot?.metric_type !== 'steps'));
  const actsDone = todayActs.filter(a => a.status === 'completed').length;

  const complete = async (id) => {
    try { await api.post('/recovery/complete', { assignment_id: id }); toast.success('Activity completed'); load(); }
    catch { toast.error('Failed'); }
  };

  const deleteSelf = async (id) => {
    try { await api.delete(`/recovery/self-logs/${id}`); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };

  const breakdown = rec ? [
    { label: 'Sleep', val: rec.sleep, weight: rec.weights?.sleep, color: '#A78BFA' },
    { label: 'Fasting', val: rec.fasting, weight: rec.weights?.fasting, color: '#00C8FF' },
    { label: 'Meal Timing', val: rec.meal_timing, weight: rec.weights?.meal_timing, color: '#FFB800' },
    { label: 'Recovery Activities', val: rec.activities, weight: rec.weights?.activities, color: '#65A30D', excluded: rec.activities_included === false },
  ] : [];

  if (locked) return <LockedScreen title="Recovery Plan" color="#A78BFA" />;
  const ready = rec && rec.ready !== false;
  return (
    <div className="space-y-5 page-grid" data-testid="client-recovery">
      <div>
        <div className="label-mini text-[#A78BFA]">Pillar 3</div>
        <h1 className="font-display font-black text-3xl mt-1">Recovery</h1>
        <p className="text-sm text-muted-foreground mt-1">Recover better. Perform better.</p>
      </div>

      <ProgramHeader type="recovery" />

      {/* Recovery Score ring */}
      {!ready ? (
        <div className="lg-half lg-stretch regen-card-elevated p-5 blueprint-grid" data-testid="recovery-empty">
          <div className="flex items-center gap-5">
            <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: 130, height: 130 }}>
              <svg width="130" height="130">
                <circle cx="65" cy="65" r="57" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="11" />
              </svg>
              <div className="absolute flex flex-col items-center">
                <span className="font-mono text-4xl text-muted-foreground/60" data-testid="recovery-empty-value">--</span>
                <span className="text-[10px] text-muted-foreground mt-0.5">Recovery</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-lg">Not available yet</div>
              <p className="text-sm text-muted-foreground mt-1.5">Log your sleep or fasting & meal timing to generate your Recovery Score. Recovery activities add to it when you log them.</p>
            </div>
          </div>
        </div>
      ) : (
      <div className="lg-half lg-stretch regen-card-elevated p-5 blueprint-grid">
        <div className="flex items-center gap-5">
          <ScoreRing value={rec?.score ?? 0} color='#65A30D' size={130} stroke={11} label="Recovery" sub={rec?.label} />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-foreground/90 leading-relaxed">You're recovering well! Keep building these habits every day.</div>
            <div className="mt-2 flex items-center gap-3">
              {rec && <span className={`font-mono text-sm font-bold ${(rec.weekly_trend ?? 0) >= 0 ? 'text-[var(--c-green-ink)]' : 'text-[#FF5C5C]'}`} data-testid="recovery-trend">{(rec.weekly_trend ?? 0) >= 0 ? '↑ +' : '↓ '}{rec.weekly_trend ?? 0} this week</span>}
              {rec && <span className="text-xs text-muted-foreground">7-day avg: <span className="font-mono text-foreground">{rec.avg_7d}</span></span>}
            </div>
            <button onClick={() => nav('/app/insights')} data-testid="view-recovery-insights" className="mt-3 inline-flex items-center gap-1 text-[#00C8FF] text-sm font-semibold">
              View Recovery Insights <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Recovery Score Breakdown */}
      {ready ? (
      <div className="lg-half self-stretch regen-card-elevated p-5" data-testid="recovery-breakdown">
        <div className="label-mini mb-3">Recovery Score Breakdown</div>
        <div className="space-y-3">
          {breakdown.map(b => (
            <div key={b.label} className="flex items-center gap-3" data-testid={`breakdown-${b.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-32 text-sm shrink-0">{b.label}</div>
              {b.excluded ? (
                <div className="flex-1 text-[11px] text-muted-foreground">Not logged today — not counted</div>
              ) : (<>
                <div className="flex-1 h-2 rounded-full bg-[var(--c-film)] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${b.val}%`, background: b.color, boxShadow: `0 0 8px ${b.color}` }} /></div>
                <div className="font-mono text-sm w-8 text-right">{b.val}</div>
              </>)}
              <div className="text-[10px] text-muted-foreground w-8 text-right">{b.weight}%</div>
            </div>
          ))}
          {rec?.bonus > 0 && (
            <div className="flex items-center gap-3 pt-2 border-t border-[var(--c-hair)]" data-testid="recovery-bonus-row">
              <div className="w-32 text-sm text-[var(--c-green-ink)] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Self-logged</div>
              <div className="flex-1 text-xs text-muted-foreground">{rec.self_logged} bonus {rec.self_logged === 1 ? 'session' : 'sessions'}</div>
              <div className="font-mono text-sm w-8 text-right text-[var(--c-green-ink)]">+{rec.bonus}</div>
              <div className="w-8" />
            </div>
          )}
        </div>
      </div>
      ) : (
      <div className="lg-half self-stretch regen-card-elevated p-5" data-testid="recovery-breakdown-empty">
        <div className="label-mini mb-3">Recovery Score Breakdown</div>
        <div className="space-y-3">
          {['Sleep', 'Fasting', 'Meal Timing', 'Recovery Activities'].map(label => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-32 text-sm text-muted-foreground">{label}</div>
              <div className="flex-1 h-2 rounded-full bg-[var(--c-film)] overflow-hidden" />
              <div className="text-[11px] text-muted-foreground/70 whitespace-nowrap">Waiting for data</div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Recovery Summary */}
      <div>
        <div className="label-mini mb-2">Recovery Summary</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
          <SummaryCard testid="summary-sleep" icon={Moon} color="#A78BFA" label="Sleep" logged={!!sleep}
            value={sleep?.time_in_bed || '—'} sub={sleep ? `Quality ${sleep.quality}/10` : 'Tap to log'} scolor={colorOf(sleep?.duration_color)} onClick={() => nav('/app/sleep')} />
          <SummaryCard testid="summary-fasting" icon={Timer} color="#00C8FF" label="Fasting" logged={!!fasting}
            value={fasting?.fasting_duration || '—'} sub={fasting?.fasting_label || 'Tap to log'} scolor={colorOf(fasting?.fasting_color)} onClick={() => nav('/app/fasting')} />
          <SummaryCard testid="summary-lastmeal" icon={Utensils} color="#FFB800" label="Last Meal" logged={!!fasting}
            value={fasting?.last_meal_clock || '—'} sub={fasting ? `${fasting.meal_timing_label} Meal Timing` : 'Tap to log'} scolor={colorOf(fasting?.meal_timing_color)} dot={!!fasting} onClick={() => nav('/app/fasting')} />
          <SummaryCard testid="summary-activities" icon={Heart} color='#65A30D' label="Activities"
            value={`${actsDone + selfLogs.length}`} sub={selfLogs.length ? `${selfLogs.length} self-logged` : (todayActs.length ? `${actsDone}/${todayActs.length} assigned` : 'Tap to log')} scolor='#65A30D' onClick={() => activitiesRef.current?.scrollIntoView({ behavior: 'smooth' })} />
        </div>
      </div>

      {/* Recovery Activities */}
      <RecoveryProtocol />
      <GuidedBreathingList library={library} />
      <div ref={activitiesRef}>
        <div className="flex items-center justify-between mb-2">
          <div className="label-mini">Recovery Activities</div>
          <button onClick={() => setShowLogger(true)} data-testid="open-self-log-btn" className="inline-flex items-center gap-1 text-[var(--c-green-ink)] text-xs font-semibold">
            <Plus className="w-4 h-4" /> Log Activity
          </button>
        </div>
        {todayActs.length === 0 ? (
          <div className="regen-card-elevated p-6 text-center text-muted-foreground text-sm">
            <Snowflake className="w-6 h-6 mx-auto mb-2 text-[#7DD3FC]" />
            No recovery activities assigned today. Log your own below.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {todayActs.map(a => {
              const act = a.activity_snapshot || {}; const done = a.status === 'completed';
              return (
                <div key={a.id} className="regen-card-elevated p-4" data-testid={`recovery-activity-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="label-mini text-[#7DD3FC]">{act.category}</div>
                    {done && <Check className="w-4 h-4 text-[var(--c-green-ink)]" />}
                  </div>
                  <div className="font-display font-bold mt-1">{act.name}</div>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">{act.duration_min || act.target_value} min</div>
                  {!done && (
                    <button onClick={() => complete(a.id)} data-testid={`complete-recovery-${a.id}`} className="mt-3 w-full rounded-xl py-2 bg-[#7DD3FC] text-[#081726] text-sm font-semibold flex items-center justify-center gap-1.5">
                      <Check className="w-4 h-4" /> Done
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Self-logged today */}
      {selfLogs.length > 0 && (
        <div data-testid="self-logged-list">
          <div className="label-mini mb-2 text-[var(--c-green-ink)]">Logged Today (Bonus)</div>
          <div className="space-y-2">
            {selfLogs.map(s => (
              <div key={s.id} className="regen-card-elevated p-3 flex items-center gap-3" data-testid={`self-log-${s.id}`}>
                <div className="w-8 h-8 rounded-lg bg-[var(--c-green-soft)] flex items-center justify-center flex-shrink-0"><Activity className="w-4 h-4 text-[var(--c-green-ink)]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.category} · {s.duration_min} min{s.cycles ? ` · ${s.cycles} cycles` : ''}{s.notes ? ` · ${s.notes}` : ''}{s.breathing ? ' · personal, no score' : ''}</div>
                </div>
                <button onClick={() => deleteSelf(s.id)} data-testid={`delete-self-log-${s.id}`} className="text-muted-foreground hover:text-[#FF5C5C] p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Log */}
      <div>
        <div className="label-mini mb-2">Quick Log</div>
        <div className="grid grid-cols-3 gap-3">
          <QuickLog testid="quick-log-sleep" icon={Moon} color="#A78BFA" label="Log Sleep" onClick={() => nav('/app/sleep')} />
          <QuickLog testid="quick-log-mealtiming" icon={Utensils} color="#FFB800" label="Log Meal Timing" onClick={() => nav('/app/fasting')} />
          <QuickLog testid="quick-log-activity" icon={Activity} color='#65A30D' label="Log Activity" onClick={() => setShowLogger(true)} />
        </div>
      </div>

      {showLogger && <SelfLogger library={library} onClose={() => setShowLogger(false)} onSaved={() => { setShowLogger(false); load(); }} />}
    </div>
  );
}

function SelfLogger({ library, onClose, onSaved }) {
  const cats = [...new Set(library.map(a => a.category))];
  const [category, setCategory] = useState('');
  const [name, setName] = useState('');
  const [duration, setDuration] = useState(10);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const namesForCat = library.filter(a => a.category === category);

  const save = async () => {
    if (!category || !name) { toast.error('Pick a category and activity'); return; }
    setSaving(true);
    try {
      await api.post('/recovery/self-log', { category, name, duration_min: Number(duration) || 10, notes });
      toast.success('Recovery activity logged');
      onSaved();
    } catch { toast.error('Failed to log'); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" data-testid="self-logger-modal">
      <div className="regen-card-elevated w-full sm:max-w-md p-5 rounded-t-3xl sm:rounded-3xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-lg">Log Recovery Activity</div>
          <button onClick={onClose} data-testid="self-logger-close" className="text-muted-foreground p-1"><X className="w-5 h-5" /></button>
        </div>

        <div>
          <div className="label-mini mb-2">Category</div>
          <div className="flex flex-wrap gap-2">
            {cats.map(c => (
              <button key={c} onClick={() => { setCategory(c); setName(''); }} data-testid={`self-cat-${c}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${category === c ? 'bg-[var(--c-green)] text-[#081726]' : 'bg-[var(--c-film)] text-foreground/80'}`}>{c}</button>
            ))}
          </div>
        </div>

        {category && (
          <div>
            <div className="label-mini mb-2">Activity</div>
            <div className="flex flex-wrap gap-2">
              {namesForCat.map(a => (
                <button key={a.id} onClick={() => { setName(a.name); setDuration(a.duration_min || a.target_value || 10); }} data-testid={`self-act-${a.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${name === a.name ? 'bg-[#7DD3FC] text-[#081726]' : 'bg-[var(--c-film)] text-foreground/80'}`}>{a.name}</button>
              ))}
            </div>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Or type a custom activity" data-testid="self-name-input"
              className="mt-2 w-full bg-[var(--c-film)] rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-1 ring-[var(--c-green)]" />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="label-mini mb-2">Duration (min)</div>
            <input type="number" value={duration} onChange={e => setDuration(e.target.value)} data-testid="self-duration-input"
              className="w-full bg-[var(--c-film)] rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-1 ring-[var(--c-green)]" />
          </div>
          <div>
            <div className="label-mini mb-2">Notes</div>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" data-testid="self-notes-input"
              className="w-full bg-[var(--c-film)] rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-1 ring-[var(--c-green)]" />
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="self-log-save-btn"
          className="w-full rounded-xl py-3 bg-[var(--c-green)] text-[#081726] font-semibold disabled:opacity-50">{saving ? 'Saving…' : 'Log Activity'}</button>
        <p className="text-[11px] text-muted-foreground text-center">Self-logged activities add a bonus to your Recovery Score (max +10/day).</p>
      </div>
    </div>
  );
}

function SummaryCard({ testid, icon: Icon, color, label, value, sub, scolor, dot, onClick, logged }) {
  return (
    <button onClick={onClick} data-testid={testid} className="regen-card-elevated p-3 text-left active:scale-[0.98] transition">
      <div className="flex items-center gap-1.5">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-[11px] text-muted-foreground">{label}</span>
        {logged !== undefined && (
          <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[var(--c-film)]" style={{ color }} data-testid={`${testid}-action`}>{logged ? 'Edit' : 'Log'}</span>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        {dot && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: scolor, boxShadow: `0 0 6px ${scolor}` }} />}
        <div className="font-mono text-lg leading-none">{value}</div>
      </div>
      <div className="text-[11px] font-semibold mt-1" style={{ color: scolor }}>{sub}</div>
    </button>
  );
}

function QuickLog({ testid, icon: Icon, color, label, onClick }) {
  return (
    <button onClick={onClick} data-testid={testid} className="regen-card-elevated p-3 flex flex-col items-center gap-1.5 active:scale-[0.98] transition">
      <Icon className="w-5 h-5" style={{ color }} />
      <span className="text-[11px] font-medium text-center leading-tight">{label}</span>
    </button>
  );
}
